/* eslint-disable complexity */
import { getValidChildren } from '@chakra-ui/react-utils';
import { IMenuDividerProps } from '@lego/klik-ui-menu';
import * as React from 'react';
import { DropdownGroup, IDropdownGroupProps } from './DropdownGroup';
import { IDropdownOptionProps } from './DropdownOption';
import {
  IDropdownGroupType,
  IDropdownOptionItem,
  IDropdownSelectionIndicatorType,
} from './DropdownTypes';

type AllowedChildrenType = IDropdownGroupProps | IDropdownOptionProps | IMenuDividerProps;

const sortGroup = (a: IDropdownOptionItem, b: IDropdownOptionItem): number => {
  if (a._group < b._group) {
    return -1;
  }

  if (a._group > b._group) {
    return 1;
  }

  return 0;
};

type IFallbackType = {
  text: string;
  value: string;
};

type IWithProps = {
  props?: unknown | undefined;
};
type IWithChildren = {
  children?: unknown | undefined;
};

// used by both buildDeepOptionsDict and useDropdownOptionGroup.onClick
// to pick fallback $text and $value from innerHTML
export const getFallbackValues = (
  textProp: string,
  valueProp: string,
  children: React.ReactNode | React.ReactNode[]
): IFallbackType => {
  let fallbackText = textProp;
  let fallbackValue = valueProp;
  let content: string | undefined;

  if (children && typeof children === 'object') {
    const cp = children as IWithProps;

    if (cp.props) {
      const c = cp.props as IWithChildren;

      if (c.children) {
        content = c.children as string;
      }
    }
  }

  if (!content && typeof children === 'string') {
    content = children as string;
  }

  if (content) {
    // if (typeof content === 'string') {
    const innerHtmlString = content; // children as string;

    if (textProp === 'no-text') {
      // use innerHTML for text-prop
      fallbackText = innerHtmlString;
    }

    if (valueProp === 'no-value') {
      // use innerHTML for value-prop
      fallbackValue = innerHtmlString;
    }
  } else if (textProp === 'no-text' && valueProp !== 'no-value') {
    // use value as text (if the $value prop is present, but $text is not)
    fallbackText = valueProp;
  }

  const fallbacks: IFallbackType = {
    text: fallbackText,
    value: fallbackValue,
  };

  // console.log(
  //   'getFallbackValues:',
  //   textProp,
  //   valueProp,
  //   children,
  //   typeof children,
  //   '>>',
  //   fallbacks
  // );

  return fallbacks;
};

export const filterChildren = (
  children: React.ReactElement[],
  lcQuery = ''
): React.ReactElement[] => {
  return getValidChildren(children).filter((child: React.ReactElement) => {
    //
    // Note: Filter routine borrowed from chakra's use-menu hook
    // was: if ((child.type as any).id === 'MenuItemOption') {
    //
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const typeAny = child.type as any;

    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (typeAny.id === 'MenuItemOption') {
      const childProps = child.props as IDropdownOptionProps;
      const { text, value = '' } = childProps;
      const str: string = text ?? value;
      // eslint-disable-next-line no-console
      // console.log('searching ', str, 'for', lcQuery);

      return str.toLowerCase().includes(lcQuery);
    } else {
      return true;
    }
  });
};

export const buildDeepOptionsDict = (
  parentMultiple: boolean | undefined,
  children: React.ReactNode | React.ReactNode[],
  result: IDropdownOptionItem[] = [],
  count = 0
): IDropdownOptionItem[] => {
  // console.log('buildDeepOptionsDict', parentMultiple, children, result, count);

  getValidChildren(children).map((child: React.ReactElement<AllowedChildrenType>) => {
    //
    // Note: Filter routine borrowed from chakra's use-menu hook
    // was: if ((child.type as any).id === 'MenuItemOption') {
    //
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const typeAny = child.type as any;

    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (typeAny.id === 'MenuItemOption') {
      const childProps = child.props as IDropdownOptionProps;

      const { text = 'no-text', value = 'no-value' } = childProps;
      const fallbacks = getFallbackValues(text, value, childProps.children);

      const opt: IDropdownOptionItem = {
        text: fallbacks.text,
        value: fallbacks.value,
        _type: parentMultiple ? 'm' : 's',
        _group: `grp-${count}`,
      };

      result.push(opt);
    } else if (child.type === DropdownGroup) {
      const groupProps = child.props as IDropdownGroupProps;

      buildDeepOptionsDict(
        groupProps.isMultiple ?? parentMultiple,
        groupProps.children,
        result,
        count++
      );
    }
  });

  return result;
};

// ensure only IDropdownOptionItem's with unique $values remain
export const removeDuplicateSelections = (list: IDropdownOptionItem[]): IDropdownOptionItem[] => {
  const result: IDropdownOptionItem[] = [];
  const map = new Map();

  for (const item of list) {
    if (!map.has(item.value)) {
      map.set(item.value, true); // set any value to Map
      result.push(item);
    }
  }

  return result;
};

/**
 * For items grouped by $gruop:
 * - allow many items of _type=m
 * - but only a single of _type=s
 */
export const manageSelection = (
  list: IDropdownOptionItem[],
  changeSet: IDropdownOptionItem[],
  group: string
): IDropdownOptionItem[] => {
  // console.log('manageSelection', list, changeSet, group);

  if (group === 'CLEARALL') {
    return [];
  }

  if (changeSet.length === 0) {
    // remove the group
    return list.filter((listItem) => {
      return listItem._group !== group;
    });
  }

  let newSelectionList = [...list];

  for (const item of changeSet) {
    newSelectionList = newSelectionList.filter((listItem) => {
      return listItem._group !== item._group;
    });
  }

  newSelectionList = [...newSelectionList, ...changeSet];
  newSelectionList = removeDuplicateSelections(newSelectionList);

  // sort by _group
  newSelectionList.sort(sortGroup);

  return newSelectionList;
};

/**
 * Return all $value fields for a specific $group in a selection list
 */
export const selectionValuesForGroup = (
  list: IDropdownOptionItem[],
  groupid: string,
  mulitple: boolean
): string[] | string => {
  const items: string[] = [];

  list.forEach((listItem) => {
    if (listItem._group === groupid) {
      items.push(listItem.value);
    }
  });

  const result = mulitple ? items : items[0];

  return result || '';
};

/**
 * Return all $value fields in a selection list
 */
export const selectionValues = (list: IDropdownOptionItem[]): string[] => {
  return list.map((listItem) => listItem.value);
};

/**
 * Return all $text fields in a selection list
 */
export const selectionLabels = (list: IDropdownOptionItem[]): string[] => {
  return list.map((listItem) => listItem.text);
};

/**
 * Determine whether the List should stay open after a selection.
 * It should, if:
 * - the toplevel has isMultiple = true
 * - any sub-group has isMultiple = true
 */
export const getShouldCloseOnSelect = (children: unknown, multiple: boolean): boolean => {
  const numberOfOptionGroupsInChildrenWithCheckboxTypeProp = getValidChildren(
    children as React.ReactNode[]
  )
    .filter(
      (childNode): childNode is React.ReactElement<IDropdownGroupProps> =>
        childNode.type === DropdownGroup
    )
    .filter((childNode) => childNode.props.isMultiple === true).length;

  const shouldCloseOnSelect = multiple
    ? false
    : numberOfOptionGroupsInChildrenWithCheckboxTypeProp === 0;

  return shouldCloseOnSelect;
};

/**
 * Determine group type
 */
export const getGroupType = (
  parentMultiple: boolean | undefined,
  localMultiple: boolean | undefined
): IDropdownGroupType => {
  // use checkbox, if isMultiple=true is set on <Dropdown.Group>
  if (localMultiple === true) {
    return 'checkbox';
  }

  // use radio, if isMultiple=false is set on <Dropdown.Group>
  if (localMultiple === false) {
    return 'radio';
  }

  // or, if $isMultiple=true is set on the parent <Dropdown>
  if (parentMultiple === true) {
    return 'checkbox';
  }

  return 'radio';
};

/**
 * Determine group decorator, default to tick
 */
export const getGroupDecorator = (
  parentDecorator: IDropdownSelectionIndicatorType | undefined,
  localDecorator: IDropdownSelectionIndicatorType | undefined,
  parentMultiple: boolean | undefined,
  localMultiple: boolean | undefined
): IDropdownSelectionIndicatorType => {
  if (localDecorator) {
    return localDecorator;
  }

  if (parentDecorator) {
    return parentDecorator;
  }

  return 'tick';

  // if (localMultiple === true) {
  //   return 'check';
  // }

  // if (parentMultiple === true) {
  //   return 'check';
  // }

  // return 'tick';
};

/**
 * Determine wether two string[] are the same
 * Note: Works fine for the simple ['dk', 'no', ...] types we use, but not much else...
 */
export const stringArraysEqual = (a: string[], b: string[]): boolean => {
  const aa = [...a].sort().toString();
  const bb = [...b].sort().toString();

  return aa === bb;
};
